import { ArrowUp, Trophy } from 'lucide-react';

import { sessionWorkout, summarizeExercise } from '../../utils/progress';
import { LoadChart, Sparkline } from './ProgressCharts';
import { ExerciseArt } from './SessionViews';
import { useProgressFormat } from './useProgressFormat';

// How much the best set went up since the first session: "↑ 7,5 kg", "↑ 3 reps", or why there's no arrow
const Gain = ({ exercise, summary }) => {
  const format = useProgressFormat();
  const { ui } = format;
  if (summary.points.length < 2) return <span className="mt-pg-gain mt-pg-gain--flat">{ui('firstSession')}</span>;
  if (summary.gain <= 0) return <span className="mt-pg-gain mt-pg-gain--flat">{ui(exercise.isLoaded ? 'sameLoad' : 'sameReps')}</span>;
  return (
    <span className="mt-pg-gain">
      <ArrowUp aria-hidden="true" />
      <span className="mt-sr-only">{ui('up')} </span>
      {exercise.isLoaded ? `${format.number(summary.gain)} kg` : ui('reps', { count: summary.gain })}
    </span>
  );
};

/**
 * Exercícios: every exercise ever logged, trained last first, with its best set, how many sessions, a small line
 * of the best set per session and how much it went up. On desktop the open one is marked (aria-current).
 */
export const ExerciseList = ({ exercises, currentName, onOpenExercise }) => {
  const format = useProgressFormat();
  const { ui } = format;
  return (
    <div className="mt-pg-pane">
      <p className="mt-pg-pane__note">{ui('exercisesNote', { count: exercises.length })}</p>
      <ul>
        {exercises.map((exercise) => {
          const summary = summarizeExercise(exercise);
          return (
            <li key={exercise.name}>
              <button
                type="button"
                className="mt-pg-erow"
                data-exercise-link={exercise.name}
                aria-current={currentName === exercise.name ? 'true' : undefined}
                onClick={() => onOpenExercise(exercise.name)}
              >
                <ExerciseArt src={exercise.thumbnailUrl} size={48} />
                <span className="mt-pg-erow__text">
                  <b>{exercise.name}</b>
                  <small>
                    <span className="mt-pg-nw">{ui('best', { value: format.dose(summary.best.group.best) })} ·</span>{' '}
                    <span className="mt-pg-nw">{ui('sessionCount', { count: exercise.entries.length })}</span>
                  </small>
                </span>
                <span className="mt-pg-erow__trend">
                  <Sparkline values={summary.points.map((point) => point.value)} />
                  <Gain exercise={exercise} summary={summary} />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/**
 * One exercise's evolution: its best set, the estimated 1RM and the last time, how much it went up since the first
 * session, the line of the best set per session, and every session with it (each opens that session). Rendered
 * whole, in the page: nothing here scrolls on its own.
 */
export const ExerciseDetail = ({ exercise, workouts, level = 2, isDesktop, isCard, onOpenSession }) => {
  const format = useProgressFormat();
  const { ui } = format;
  const summary = summarizeExercise(exercise);
  const { best, first, last, lastRecord } = summary;
  const Title = `h${level}`;
  const Sub = `h${level + 1}`;
  const titleId = 'mt-pg-det-title';
  const bestSet = best.group.best;

  return (
    <section className={`mt-pg-det${isCard ? ' mt-pg-det--card' : ''}`} aria-labelledby={titleId}>
      <div className="mt-pg-det__head">
        <ExerciseArt src={exercise.thumbnailUrl} size={isDesktop ? 76 : 64} />
        <div>
          <Title id={titleId} className="mt-pg-det__title">
            {exercise.name}
          </Title>
          <p>{ui('sessionsSince', { count: exercise.entries.length, date: format.shortDate(exercise.entries[0].date) })}</p>
        </div>
      </div>

      <div className="mt-pg-hero">
        <p className="mt-pg-hero__num">
          {bestSet.weight > 0 ? (
            <>
              {format.number(bestSet.weight)}
              <small> {bestSet.unit}</small> <span>× {bestSet.reps}</span>
            </>
          ) : (
            <>
              {bestSet.reps}
              <small> reps</small>
            </>
          )}
          <span className="mt-sr-only"> {ui('bestSetLabel')}</span>
        </p>
        <p className="mt-pg-hero__side">
          {exercise.isLoaded && summary.oneRM > 0 && (
            <span>
              {ui('oneRM')} <b>{format.number(summary.oneRM)} kg</b>
            </span>
          )}
          <span>
            {ui('lastTime')} <b>{format.dose(last.group.best)}</b>
          </span>
        </p>
        <p className="mt-pg-hero__cap">
          {summary.gain > 0 ? (
            <>
              <Gain exercise={exercise} summary={summary} /> {ui('sinceFirst', { dose: format.dose(first.group.best) })}
            </>
          ) : (
            ui('bestSoFar')
          )}
          {lastRecord && ` ${ui('lastRecord', { date: format.shortDate(lastRecord.date) })}`}
        </p>
      </div>

      {summary.points.length > 0 && (
        <div className="mt-pg-line">
          <div className="mt-pg-line__head">
            <Sub>{ui(exercise.isLoaded ? 'loadChart' : 'repsChart')}</Sub>
            <span className="mt-pg-legend" aria-hidden="true">
              <span>
                <i />
                {ui('legendSession')}
              </span>
              <span>
                <i className="mt-pg-legend__record" />
                {ui('recordShort')}
              </span>
            </span>
          </div>
          <LoadChart exercise={exercise} summary={summary} isDesktop={isDesktop} />
        </div>
      )}

      <div className="mt-pg-hist">
        <Sub>{ui('allTimes')}</Sub>
        <ul>
          {[...exercise.entries].reverse().map((entry) => {
            const sets = entry.group.workingSets.length ? entry.group.workingSets : entry.group.sets;
            return (
              <li key={entry.log.id}>
                <button type="button" className="mt-pg-hrow" onClick={() => onOpenSession(entry.log.id)}>
                  <b>
                    {format.dayName(entry.date)} <span>· {format.workoutTitle(sessionWorkout(entry.log, workouts).name)}</span>
                  </b>
                  <small>{sets.map(format.compactSet).join(' · ')}</small>
                  <span className="mt-pg-hrow__best">
                    {entry.record && (
                      <>
                        <Trophy aria-hidden="true" />
                        <span className="mt-sr-only">{ui('recordShort')}, </span>
                      </>
                    )}
                    {format.dose(entry.group.best)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};
